import { CloseNotificationButton } from '@openx/components-core/lib/Snackbar/CloseNotificationButton.tsx';
import type { SnackbarKey } from 'notistack';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  getDealInventoryListPaths,
  getPackageInventoryListPaths,
  hasInventoryListValues,
  resetDealInventoryLists,
  resetPackageInventoryLists,
} from './inventoryListResetUtils.ts';

type FormType = 'deal' | 'package';

type UseTargetingResetOnAccountChangeReturn = {
  resetTargeting: () => void;
};

export const useTargetingResetOnAccountChange = (formType: FormType): UseTargetingResetOnAccountChangeReturn => {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { setValue, getValues } = useFormContext();

  const isDealForm = formType === 'deal';

  const resetTargeting = useCallback((): void => {
    const inventoryPaths = isDealForm ? getDealInventoryListPaths() : getPackageInventoryListPaths();
    const hasInventoryValues = hasInventoryListValues(getValues, inventoryPaths);
    const audiencePath = isDealForm ? 'targeting.targeting.audience' : 'targeting.audience';
    const contextualPath = isDealForm ? 'targeting.targeting.contextual' : 'targeting.contextual';

    if (hasInventoryValues) {
      if (isDealForm) {
        resetDealInventoryLists(setValue);
      } else {
        resetPackageInventoryLists(setValue);
      }

      const message = t(
        'Inventory lists have been removed due to organization change. Please reselect inventory lists if needed.'
      );

      enqueueSnackbar(message, {
        action: (key: SnackbarKey) => <CloseNotificationButton action={() => closeSnackbar(key)} />,
        variant: 'warning',
        autoHideDuration: 5000,
      });
    }

    setValue(contextualPath, []);
    setValue(audiencePath, null);
  }, [t, enqueueSnackbar, closeSnackbar, setValue, getValues, isDealForm]);

  return { resetTargeting };
};
